<template>
  <!-- color: a small swatch alongside the value -->
  <div v-if="isColor" class="d-inline-flex align-items-center gap-2">
    <span class="adjustment-color-swatch" :style="{ backgroundColor: String(value) }" />
    <code class="text-muted">{{ value }}</code>
  </div>

  <!-- image: a thumbnail of the picked image, falling back to the hash when it can't be resolved -->
  <div v-else-if="hash" class="adjustment-value-image">
    <img v-if="previewUrl" class="adjustment-value-thumb" :src="previewUrl" :alt="def.properties.label || ''" />
    <span v-else-if="isLoadingPreview" class="adjustment-value-thumb adjustment-value-thumb-empty">
      <span class="spinner-border spinner-border-sm text-muted" role="status" />
    </span>
    <span v-else class="adjustment-value-thumb adjustment-value-thumb-empty">
      <i class="ti ti-photo-off icon"></i>
    </span>
    <code v-if="!previewUrl && !isLoadingPreview" class="text-muted text-truncate">{{ hash }}</code>
  </div>

  <!-- structured list (e.g. benefits): title, subtitle and a rendered icon per item -->
  <ul v-else-if="listItems" class="adjustment-value-list">
    <li v-for="(item, index) in listItems" :key="index" class="adjustment-value-item">
      <span v-if="item.iconSvg" class="adjustment-value-icon" v-html="item.iconSvg" />
      <img v-else-if="item.iconUrl" class="adjustment-value-icon" :src="item.iconUrl" alt="" />
      <span class="adjustment-value-text">
        <span v-if="item.title" class="adjustment-value-title">{{ item.title }}</span>
        <span v-if="item.subtitle" class="adjustment-value-subtitle text-muted">{{ item.subtitle }}</span>
      </span>
    </li>
  </ul>

  <!-- everything else: compact textual value -->
  <span v-else class="text-muted">{{ text }}</span>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { hostname } from '@/api/client';
import type { TemplateOptionDef } from '@/types/templateMeta';
import { formatOptionValue, iconRef, imageHash, resolveImagePreviewUrl } from './adjustmentHelpers';

const props = defineProps<{
  def: TemplateOptionDef;
  value: unknown;
}>();

const isColor = computed(() => props.def?.type === 'input.color' && typeof props.value === 'string' && !!props.value);

// image options are stored as { hash, focus? } — the thumbnail url has to be looked up by hash
const hash = computed(() => imageHash(props.value));
const previewUrl = ref('');
const isLoadingPreview = ref(false);

watch(
  hash,
  async (current) => {
    previewUrl.value = '';
    if (!current) {
      isLoadingPreview.value = false;
      return;
    }

    isLoadingPreview.value = true;
    const url = await resolveImagePreviewUrl(current);
    // a newer hash may have won the race while this lookup was in flight
    if (hash.value !== current) return;
    previewUrl.value = url;
    isLoadingPreview.value = false;
  },
  { immediate: true }
);

// benefit-style complex values: an array of objects carrying title/subtitle/icon. the icon is
// either an inline SVG string (used as-is) or a Tabler reference like "outline/clock", served as an
// svg by the pjas backend proxy at /v1/icons/{variant}/{name} — the same source the template uses.
const listItems = computed(() => {
  if (props.def?.type !== 'input.complex' || !Array.isArray(props.value) || props.value.length === 0) {
    return null;
  }

  const items = props.value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
  if (!items.length || !items.some((item) => 'title' in item || 'subtitle' in item || 'icon' in item)) {
    return null;
  }

  return items.map((item) => {
    const icon = typeof item.icon === 'string' ? item.icon : '';
    const ref = iconRef(icon);
    return {
      title: typeof item.title === 'string' ? item.title : '',
      subtitle: typeof item.subtitle === 'string' ? item.subtitle : '',
      iconSvg: icon.trim().startsWith('<svg') ? icon : '',
      iconUrl: ref ? `${hostname}/v1/icons/${ref}` : null,
    };
  });
});

const text = computed(() => formatOptionValue(props.value));
</script>

<style scoped>
.adjustment-color-swatch {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: inline-block;
  flex-shrink: 0;
}

.adjustment-value-image {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.adjustment-value-thumb {
  width: 64px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid var(--tblr-border-color, #eaecf0);
  object-fit: contain;
  background: var(--tblr-body-bg, #fff);
}

.adjustment-value-thumb-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tblr-secondary, #6c757d);
}

.adjustment-value-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.adjustment-value-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.adjustment-value-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tblr-secondary, #6c757d);
}

.adjustment-value-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.adjustment-value-text {
  min-width: 0;
}

.adjustment-value-title {
  display: block;
  font-weight: 500;
  color: #101828;
  font-size: 0.8125rem;
}

.adjustment-value-subtitle {
  display: block;
  font-size: 0.8125rem;
}
</style>
